import { useEffect, useId, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faClose } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";

export interface NavItem {
    label: string;
    to: string;
}

interface HamburgerMenuProps {
    navItems: NavItem[];
    className?: string;
    toggleLabel?: string; // Accessible name of the button
}

// Links in a row on tablets and up, a dropdown behind a button on phones
const HamburgerMenu = ({ navItems, className, toggleLabel = "Toggle menu" }: HamburgerMenuProps) => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const panelId = useId();

    // Close after navigating (also when the page changes by Back/Forward)
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Escape closes the menu
    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open]);

    return (
        <nav className={classNames("w-full lg:w-fit", className)}>
            {/* Desktop nav */}
            <ul className="hidden md:flex relative items-center mr-0 lg:mr-20 list-none sm:gap-2 md:gap-8 xl:gap-16 justify-center text-xl transition">
                {navItems.map(({ label, to }) => (
                    <li key={to}>
                        <Link to={to} className="text-gray-700 hover:text-black">
                            {label}
                        </Link>
                    </li>
                ))}
            </ul>

            {/* Mobile toggle button */}
            <div className="md:hidden">
                <button
                    type="button"
                    onClick={() => setOpen((isOpen) => !isOpen)}
                    aria-label={toggleLabel}
                    aria-expanded={open}
                    aria-controls={panelId}
                    className="p-2 text-gray-700 focus:outline-none absolute right-5 top-3"
                >
                    <FontAwesomeIcon icon={open ? faClose : faBars} className="h-8 w-8" />
                </button>
            </div>

            {/* Mobile nav dropdown */}
            {open && (
                <div
                    id={panelId}
                    className="md:hidden absolute w-full top-20 right-0 z-40 bg-white shadow-md py-4 px-6 rounded-b-lg"
                >
                    <ul className="flex flex-col gap-4 list-none text-xl">
                        {navItems.map(({ label, to }) => (
                            <li key={to}>
                                <Link
                                    to={to}
                                    className="block text-gray-800 hover:text-black transition"
                                >
                                    {label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </nav>
    );
};

export default HamburgerMenu;